import { TriangleAlert } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';

/**
 * Maximum number of messages we consider reasonable on a single outreach
 * sequence or shift-confirmation flow. Adding a message that would push the
 * total past this many triggers an "are you sure" confirmation at the moment
 * of the add, before the step or follow-up is appended.
 *
 * The count is per-surface: outreach and settings sequences count total steps
 * (SMS + voice); shift confirmations count follow-up messages (the initial
 * message is separate). In every case adding the item that makes the count
 * exceed the threshold (the 4th) is what prompts.
 *
 * This is a best-guess default with no product-defined number yet. Keep it in
 * sync with the backend "spammy scheduler behavior" Slack alert (DEP-312),
 * which flags the same condition server-side.
 */
export const SPAMMY_FOLLOW_UP_THRESHOLD = 3;

type PendingConfirmation = {
    readonly resolve: (confirmed: boolean) => void;
    readonly nextCount: number;
};

type SpammyFollowUpConfirmation = {
    /**
     * `nextCount` is the number of messages the sequence would have after the
     * add. Resolves `true` immediately when that stays within the threshold.
     * Above the threshold it opens the confirmation dialog and resolves `true`
     * only if the user confirms the add, `false` if they cancel.
     */
    readonly confirmAddingStep: (nextCount: number) => Promise<boolean>;
    readonly spammyFollowUpConfirmationDialog: ReactNode;
};

/** Request acknowledgement when adding a message would make a config spammy. */
export function useSpammyFollowUpConfirmation(): SpammyFollowUpConfirmation {
    const [pendingConfirmation, setPendingConfirmation] =
        useState<PendingConfirmation | null>(null);

    const confirmAddingStep = useCallback((nextCount: number) => {
        if (nextCount <= SPAMMY_FOLLOW_UP_THRESHOLD) {
            return Promise.resolve(true);
        }
        return new Promise<boolean>((resolve) => {
            setPendingConfirmation({ resolve, nextCount });
        });
    }, []);

    const finishConfirmation = (confirmed: boolean) => {
        pendingConfirmation?.resolve(confirmed);
        setPendingConfirmation(null);
    };

    const confirmationDialog = (
        <Dialog
            open={pendingConfirmation !== null}
            onOpenChange={(open) => {
                if (!open) {
                    finishConfirmation(false);
                }
            }}
        >
            <DialogContent>
                <DialogHeader className="text-left">
                    <DialogTitle className="flex items-center gap-2">
                        <TriangleAlert className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        Are you sure?
                    </DialogTitle>
                    <DialogDescription className="text-left">
                        {`This would make ${pendingConfirmation?.nextCount ?? 0} messages, more than the ${SPAMMY_FOLLOW_UP_THRESHOLD} we recommend. Sending this many messages can feel spammy to caregivers and hurt response rates. Do you want to add it anyway?`}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => finishConfirmation(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => finishConfirmation(true)}
                    >
                        Add anyway
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return {
        confirmAddingStep,
        spammyFollowUpConfirmationDialog: confirmationDialog,
    };
}
